// eslint-disable-line import/no-webpack-loader-syntax
//@ts-ignore
import mapboxgl from "mapbox-gl";
import { observer, useLocalObservable } from "mobx-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";

import "./App.scss";

const ScrollContext = createContext<LocBox | undefined>(undefined);
const getBang = () => useContext(ScrollContext)!;

const createBox = () => {
  return {
    loc: undefined as Location | undefined,

    setLoc(loc: Location) {
      this.loc = loc;
    },

    delLoc() {
      this.loc = undefined;
    },
  };
};

mapboxgl.accessToken =
  "pk.eyJ1IjoiZ2F2aW5sZXJveSIsImEiOiJjbHc0M2pvYmsxNG5xMmlwN3Rubmc4YmhvIn0.WeAhCd-gVhf4ri49FpV6_Q";

type MarkerKind = "school" | "home" | "work";

interface LocBox {
  loc: Location | undefined;
  setLoc: (loc: Location) => void;
  delLoc: () => void;
}

type Location = {
  name: string;
  coords: [number, number];
  kind: MarkerKind;
  duration?: string;
  description?: string;
};

const locations: Location[] = [
  { name: "Providence, RI", coords: [-71.4029, 41.8268], kind: "school" },
  {
    name: "Zürich, Switzerland",
    coords: [8.5476, 47.3764],
    kind: "school",
    duration: "3 years",
  },
  {
    name: "Madison, WI",
    coords: [-89.4125, 43.0766],
    kind: "work",
    duration: "4 months",
  },
  {
    name: "Salt Lake City, UT",
    coords: [-111.8421, 40.7649],
    kind: "school",
    duration: "Way too long",
  },
  {
    name: "Curitiba, Brazil",
    coords: [-49.2652, -25.4269],
    kind: "work",
    duration: "1.5 years",
  },
  {
    name: "Logan, UT",
    coords: [-111.8338, 41.737],
    kind: "school",
    duration: "2 years",
  },
];

const Scroll = observer(() => {
  const info = getBang();
  if (!info.loc) return null;

  const capFirst = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const Row = ({ label, value }: { label: string; value: string }) => (
    <tr>
      <td>{label}</td>
      <td>{value}</td>
    </tr>
  );

  return (
    <div className="scroll">
      <div className="scroll-inner">
        <div className="scroll-title">{info.loc.name}</div>
        <table>
          <tbody>
            <Row label="Latitude" value={info.loc.coords[0].toString()} />
            <Row label="Longitude" value={info.loc.coords[1].toString()} />
            <Row label="Activity" value={capFirst(info.loc.kind)} />
            {info.loc.duration && (
              <Row label="Duration" value={info.loc.duration} />
            )}
          </tbody>
        </table>

        <p className="scroll-description">{info.loc.description}</p>
      </div>
    </div>
  );
});

const FallbackComponent = ({
  error: _e,
  resetErrorBoundary: _r,
}: {
  error: any;
  resetErrorBoundary: any;
}) => {
  return (
    <div className="error">
      <h1>Something went wrong</h1>
      <p>But don’t worry, we’re on it!</p>
    </div>
  );
};

const Marker = observer(({ location }: { location: Location }) => {
  return <div className={`marker ${location.kind}`}></div>;
});

const Map = observer(() => {
  const box = getBang();
  const mapContainer = useRef(null);
  const map = useRef<mapboxgl.Map>(null);
  const markers = useRef<HTMLDivElement[]>([]);

  const lng = -71.4029;
  const lat = 41.8268;
  const zoom = 9;

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/gavinleroy/clw2aoaof017x01ob2w26acqf",
      center: [lng, lat],
      zoom: zoom,
    });

    markers.current = locations.map(location => {
      // Create a new DOM node and save it to the React ref
      const div = document.createElement("div");
      createRoot(div).render(<Marker location={location} />);
      div.addEventListener("mouseenter", () => {
        box.setLoc(location);
      });
      div.addEventListener("mouseleave", () => {
        box.delLoc();
      });
      new mapboxgl.Marker(div).setLngLat(location.coords).addTo(map.current!);
      return div;
    });
  });

  return <div ref={mapContainer} className="map-container" />;
});

const BorderedDiv = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bordered-outer">
      <div className="bordered-inner">{children}</div>
    </div>
  );
};

const Screen = () => {
  return (
    <>
      <BorderedDiv>
        <div className="inner-content">
          <div className="title">Oh, The Places You’ll Live</div>
          <ErrorBoundary FallbackComponent={FallbackComponent}>
            <Map />
          </ErrorBoundary>
        </div>
      </BorderedDiv>
      <Scroll />
    </>
  );
};

const App = () => {
  const box = useLocalObservable(createBox);
  return (
    <ScrollContext.Provider value={box}>
      <Screen />
    </ScrollContext.Provider>
  );
};

export default App;
